// Phase G+ — Social chat over the LiveKit data channel.
//
// Pure wire-format contract for inbound external-platform chat (YouTube,
// Twitch, X, …) relayed into an on-platform room. NO vendor SDK import — the
// bridge that publishes (tools/ssn-livekit-bridge) and the client that renders
// both depend only on this module, so the two can never drift on the wire
// shape. Same no-vendor rule as the room-service seam (see ./types.ts).

/** Data-channel topic every social-chat packet is published and filtered under. */
export const SOCIAL_CHAT_TOPIC = 'social';

/**
 * Identity the relay bridge joins the room under. Source of truth for the
 * origin gate: a client renderer must accept a social-chat packet ONLY when the
 * publishing participant's identity equals this. The `social` topic is writable
 * by any participant with `canPublishData` (every host/guest), so without this
 * check a guest could publish forged chat. The `livekit-relay-token` Edge
 * Function hard-codes the same literal (Deno edge can't import this package) and
 * `livekit-token` refuses to mint it to a normal user — keep all three in sync.
 */
export const SOCIAL_RELAY_IDENTITY = 'social-relay';

/**
 * One relayed chat message from an external platform. Versioned (`v`) so the
 * renderer can reject or migrate older shapes if the wire format ever changes.
 */
export interface SocialChatMessage {
  /** Wire-format version. Bump on any breaking shape change. */
  v: 1;
  /** Discriminant — guards against decoding an unrelated data packet as chat. */
  kind: 'social-chat';
  /** Stable per-message id from the source platform; used for dedupe. */
  id: string;
  /** Source platform slug, e.g. 'youtube' | 'twitch' | 'x'. Free-form. */
  platform: string;
  author: {
    name: string;
    handle?: string;
    avatarUrl?: string;
    /** Author display colour when the platform supplies one (e.g. Twitch). */
    color?: string;
  };
  text: string;
  /** Source timestamp, epoch ms. */
  ts: number;

  // --- Native chat (platform === 'archetelos') — additive, stays v:1. Absent on
  // relayed external messages. See docs/2026-07-04-native-chat/SPEC.md.
  /** Sender's profile uuid — native only; the moderation target (delete/mute). */
  senderId?: string;
  /** Server-derived room role. Drives the HOST tag; viewers omit it entirely. */
  role?: 'host' | 'cohost';
  /** Echoed client nonce for optimistic-send matching. Never persisted. */
  nonce?: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Serialise a message to a UTF-8 JSON byte payload for `publishData`. */
export function encodeSocialChat(msg: SocialChatMessage): Uint8Array {
  return encoder.encode(JSON.stringify(msg));
}

/**
 * Parse + validate a received data payload. Returns the message, or `null` for
 * anything malformed — a packet on another topic, truncated bytes, bad JSON, or
 * a shape that fails validation. Callers render only non-null results, so this
 * is the single shape-trust gate on third-party content reaching the client.
 * (Content sanitisation — escaping, length — is the renderer's/bridge's job;
 * this validates structure, not policy.)
 */
export function decodeSocialChat(bytes: Uint8Array): SocialChatMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes));
  } catch {
    return null;
  }
  return isSocialChatMessage(parsed) ? parsed : null;
}

/**
 * Structural type guard for the social-chat shape. Exported so the union decoder
 * in `superchat.ts` can delegate to it after discriminating on `kind` — one guard
 * per shape, no parallel schema.
 */
export function isSocialChatMessage(value: unknown): value is SocialChatMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  if (m.v !== 1 || m.kind !== 'social-chat') return false;
  if (typeof m.id !== 'string' || m.id === '') return false;
  if (typeof m.platform !== 'string' || m.platform === '') return false;
  if (typeof m.text !== 'string') return false;
  if (typeof m.ts !== 'number' || !Number.isFinite(m.ts)) return false;

  const author = m.author;
  if (typeof author !== 'object' || author === null) return false;
  const a = author as Record<string, unknown>;
  if (typeof a.name !== 'string' || a.name === '') return false;
  for (const key of ['handle', 'avatarUrl', 'color'] as const) {
    if (a[key] !== undefined && typeof a[key] !== 'string') return false;
  }

  // Native additive fields — validated only when present (relayed omits them).
  if (m.senderId !== undefined && (typeof m.senderId !== 'string' || !UUID_RE.test(m.senderId))) {
    return false;
  }
  if (m.role !== undefined && m.role !== 'host' && m.role !== 'cohost') return false;
  if (m.nonce !== undefined && (typeof m.nonce !== 'string' || m.nonce.length > 64)) return false;

  return true;
}

/** Loose uuid shape check for `senderId` (defense-in-depth; the DB is canonical). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Server-origin control message: remove a chat message everywhere (soft-deleted
 * server-side; this tells live clients to drop it from the feed). Rides the same
 * `social` topic; only the server publishes it (participant-less on the client).
 */
export interface ChatDeleteMessage {
  v: 1;
  kind: 'chat-delete';
  /** The `live_chat_messages.id` (= the published SocialChatMessage id) to drop. */
  id: string;
  ts: number;
}

/** Serialise a chat-delete control message for `sendData`. */
export function encodeChatDelete(msg: ChatDeleteMessage): Uint8Array {
  return encoder.encode(JSON.stringify(msg));
}

/** Structural guard for the chat-delete control shape. */
export function isChatDeleteMessage(value: unknown): value is ChatDeleteMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  if (m.v !== 1 || m.kind !== 'chat-delete') return false;
  if (typeof m.id !== 'string' || m.id === '') return false;
  if (typeof m.ts !== 'number' || !Number.isFinite(m.ts)) return false;
  return true;
}
